import { ADHA_JOURNEY_LOG_PREFIX } from '@/core/constants/adhaJourney';
import { logger } from '@/core/logger';
import { peekStoredLanguage } from '@/core/utils/i18n';
import { getSecureRandomHex } from '@/core/wrappers/secureRandom';
import {
  ADHA_UAE_PASS_CALLBACK_PATH,
  ADHA_UAE_PASS_OAUTH_STATE_KEY,
  ADHA_UAE_PASS_PENDING_RESULT_KEY,
  type AdhaUaePassNativePendingUi,
  type AdhaUaePassPendingResult,
  type AdhaUaePassRawCallback,
  getAdhaUaePassOAuthConfig,
} from '@/modules/auth/constants/adhaUaePass';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import {
  mapAdhaUaePassError,
  adhaUaePassMessageKeyToLabel,
  ADHA_UAE_PASS_MESSAGE_KEYS,
} from '@/modules/auth/constants/adhaUaePassError.map';

export type AdhaUaePassCallbackParams = AdhaUaePassRawCallback;

function firstParam(value: string | string[] | undefined): string {
  if (Array.isArray(value)) {
    return value[0] ?? '';
  }
  return value ?? '';
}

function generateOAuthState(): string {
  return `${Date.now().toString(36)}_${getSecureRandomHex(16)}`;
}

/** In-memory OAuth `state` for native (no localStorage). Web still uses localStorage. */
let memoryOAuthState: string | null = null;

/** Native login screen handoff after WebView (dialog vs inline). */
let nativePendingUi: AdhaUaePassNativePendingUi | null = null;

function toUaePassUiLocale(language: string | null | undefined): 'en' | 'ar' {
  return language === 'ar' ? 'ar' : 'en';
}

function getAuthorizeEndpoint(baseUrl: string): string {
  const trimmed = baseUrl.replace(/\/$/, '');
  if (trimmed.endsWith('/idshub')) {
    return `${trimmed}/authorize`;
  }
  return `${trimmed}/idshub/authorize`;
}

class AdhaUaePassService {
  buildAuthorizeUrl(
    state: string,
    overrides?: {
      acrValues?: string;
      redirectUri?: string;
      clientId?: string;
      uiLocale?: 'en' | 'ar';
    },
  ): string {
    const config = getAdhaUaePassOAuthConfig();
    const redirectUri = overrides?.redirectUri || config.redirectUri;
    const acrValues = overrides?.acrValues || config.acrValues;
    const clientId = overrides?.clientId || config.clientId;

    const query = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: config.scope,
      state,
      acr_values: acrValues,
    });

    query.set('prompt', 'login');
    if (overrides?.uiLocale === 'en' || overrides?.uiLocale === 'ar') {
      query.set('ui_locales', overrides.uiLocale);
    }

    return `${getAuthorizeEndpoint(config.baseUrl)}?${query.toString()}`;
  }

  storeOAuthState(state: string): void {
    memoryOAuthState = state;
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(ADHA_UAE_PASS_OAUTH_STATE_KEY, state);
  }

  clearOAuthState(): void {
    memoryOAuthState = null;
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.removeItem(ADHA_UAE_PASS_OAUTH_STATE_KEY);
  }

  validateOAuthState(state: string): boolean {
    if (typeof localStorage !== 'undefined') {
      const storedState = localStorage.getItem(ADHA_UAE_PASS_OAUTH_STATE_KEY);
      if (!storedState) {
        logger.warn(`${ADHA_JOURNEY_LOG_PREFIX}:oauth missing stored state`);
        return false;
      }
      return storedState === state;
    }
    if (!memoryOAuthState) {
      logger.warn(`${ADHA_JOURNEY_LOG_PREFIX}:oauth missing stored state`);
      return false;
    }
    return memoryOAuthState === state;
  }

  setNativePendingUi(pending: AdhaUaePassNativePendingUi | null): void {
    nativePendingUi = pending;
  }

  consumeNativePendingUi(): AdhaUaePassNativePendingUi | null {
    const pending = nativePendingUi;
    nativePendingUi = null;
    return pending;
  }

  /**
   * Native WebView: build authorize URL with CSRF `state`.
   * Optional ACR / redirect overrides are native-only (web still uses startWebAuthorization).
   */
  startNativeAuthorization(overrides?: {
    acrValues?: string;
    redirectUri?: string;
    clientId?: string;
    uiLocale?: 'en' | 'ar';
  }): { authUrl: string; redirectUri: string } {
    this.clearPendingWelcomeResult();
    const config = getAdhaUaePassOAuthConfig();
    const redirectUri = overrides?.redirectUri || config.redirectUri;
    const state = generateOAuthState();
    const authUrl = this.buildAuthorizeUrl(state, {
      ...(overrides?.acrValues ? { acrValues: overrides.acrValues } : {}),
      ...(overrides?.clientId ? { clientId: overrides.clientId } : {}),
      ...(overrides?.uiLocale ? { uiLocale: overrides.uiLocale } : {}),
      redirectUri,
    });
    this.storeOAuthState(state);
    logger.info(`${ADHA_JOURNEY_LOG_PREFIX}:oauth native webview`, {
      callbackPath: ADHA_UAE_PASS_CALLBACK_PATH,
      authUrl,
    });
    return { authUrl, redirectUri };
  }

  /** `/uae-pass` only: stash raw query params (no validation). */
  storeRawCallbackParams(raw: AdhaUaePassRawCallback): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.setItem(ADHA_UAE_PASS_PENDING_RESULT_KEY, JSON.stringify(raw));
  }

  clearPendingWelcomeResult(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.removeItem(ADHA_UAE_PASS_PENDING_RESULT_KEY);
  }

  hasPendingRawCallback(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    return sessionStorage.getItem(ADHA_UAE_PASS_PENDING_RESULT_KEY) != null;
  }

  /**
   * Welcome only: read raw callback, validate/map, return UI result.
   */
  consumeAndResolvePendingCallback(): AdhaUaePassPendingResult | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    const stored = sessionStorage.getItem(ADHA_UAE_PASS_PENDING_RESULT_KEY);
    if (!stored) {
      return null;
    }
    sessionStorage.removeItem(ADHA_UAE_PASS_PENDING_RESULT_KEY);

    let raw: AdhaUaePassRawCallback;
    try {
      raw = JSON.parse(stored) as AdhaUaePassRawCallback;
    } catch {
      logger.warn(`${ADHA_JOURNEY_LOG_PREFIX}:oauth invalid pending raw callback`);
      return {
        openLogin: true,
        errorKey: ADHA_UAE_PASS_MESSAGE_KEYS.ERROR_GENERIC,
        error: adhaUaePassMessageKeyToLabel(ADHA_UAE_PASS_MESSAGE_KEYS.ERROR_GENERIC),
      };
    }

    return this.resolvePendingResultFromCallback(raw);
  }

  /** Same-tab redirect to UAE Pass authorize URL. */
  startWebAuthorization(): void {
    this.clearPendingWelcomeResult();

    const state = generateOAuthState();
    const uiLocale = toUaePassUiLocale(peekStoredLanguage());
    const authUrl = this.buildAuthorizeUrl(state, { uiLocale });
    this.storeOAuthState(state);

    logger.info(`${ADHA_JOURNEY_LOG_PREFIX}:oauth same-tab redirect`, {
      callbackPath: ADHA_UAE_PASS_CALLBACK_PATH,
      authUrl,
    });

    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.AUTH,
      actionName: ALMETLAA_ACTION.AUTH.UAE_PASS_START_CLICK,
      eventType: 'action',
    });

    if (typeof window === 'undefined') {
      throw new TypeError('ADHA UAE Pass OAuth is only available on web');
    }

    window.location.assign(authUrl);
  }

  /** Welcome-side validation + error mapping. */
  resolvePendingResultFromCallback(
    params: AdhaUaePassCallbackParams,
  ): AdhaUaePassPendingResult {
    const oauthError = firstParam(params.error);
    const oauthErrorDescription = firstParam(params.error_description);
    const code = firstParam(params.code);
    const state = firstParam(params.state);

    if (oauthError) {
      this.clearOAuthState();
      const mapped = mapAdhaUaePassError(oauthError, oauthErrorDescription);
      logger.warn(`${ADHA_JOURNEY_LOG_PREFIX}:oauth provider error`, {
        oauthError,
        oauthErrorDescription: oauthErrorDescription || undefined,
        mappedCode: mapped.code,
        state: state || undefined,
      });
      return {
        openLogin: true,
        ...(mapped.messageKey
          ? {
              errorKey: mapped.messageKey,
              error: adhaUaePassMessageKeyToLabel(mapped.messageKey),
            }
          : {}),
      };
    }

    // OAuth redirect without code → return to login quietly (login API missing_code uses dialog).
    if (!code) {
      this.clearOAuthState();
      return { openLogin: true };
    }

    if (!state || !this.validateOAuthState(state)) {
      this.clearOAuthState();
      return {
        openLogin: true,
        errorKey: ADHA_UAE_PASS_MESSAGE_KEYS.ERROR_INVALID_SESSION,
        error: adhaUaePassMessageKeyToLabel(
          ADHA_UAE_PASS_MESSAGE_KEYS.ERROR_INVALID_SESSION,
        ),
      };
    }

    this.clearOAuthState();
    logger.info(`${ADHA_JOURNEY_LOG_PREFIX}:oauth callback success`);
    return { openLogin: true, code };
  }

  /**
   * `/uae-pass` only: stash raw query params. Caller navigates with router (no full reload).
   */
  stashRawCallbackFromWindow(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    this.storeRawCallbackParams({
      ...(code ? { code } : {}),
      ...(state ? { state } : {}),
      ...(error ? { error } : {}),
      ...(errorDescription ? { error_description: errorDescription } : {}),
    });
  }
}

export const adhaUaePassService = new AdhaUaePassService();
